import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserAddress, setLocation } from "../redux/actions/action-creator";
import { hasAuthenticatedSession, isGuestUser, isSuccessResponse } from "../utils/guestLogin";
import {
  OPEN_DELIVERY_LOCATION_EVENT,
  detectDeliveryLocation,
  hasSelectedDeliveryLocation,
  searchDeliveryLocations,
} from "../utils/locationDefaults";
import "./delivery-location-prompt.css";

const getAddresses = (response) => {
  const data = response?.data;
  return [data, data?.addresses, data?.data, data?.response_data, data?.cell_contents]
    .find(Array.isArray) || [];
};

const toAddressLocation = (address) => ({
  ...address,
  latitude: address.latitude ?? address.lat,
  longitude: address.longitude ?? address.lng,
  selected_address_id: address.id || address.address_id || address.user_address_id,
  selected_from_address_list: true,
  delivery_location_selected: true,
});

export default function DeliveryLocationPrompt({ enabled = true }) {
  const dispatch = useDispatch();
  const { userData, user_location } = useSelector((state) => state.auth || {});
  const session = String(userData?.id || "session");
  const guest = isGuestUser(userData);
  const hasSession = hasAuthenticatedSession(userData);
  const [dismissedSession, setDismissedSession] = useState(null);
  const [requested, setRequested] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesFailed, setAddressesFailed] = useState(false);
  const dialogRef = useRef(null);
  const addressRequestRef = useRef(null);
  const operationRef = useRef(0);
  const selectionCallbackRef = useRef(null);
  const titleId = useId();
  const inputId = useId();
  const open = enabled && hasSession && (requested || (
    (!hasSelectedDeliveryLocation(user_location) || (
      user_location?.location_owner_id && String(user_location.location_owner_id) !== session
    )) && dismissedSession !== session
  ));

  useEffect(() => {
    if (!enabled) {
      selectionCallbackRef.current = null;
      setDismissedSession(null);
      setRequested(false);
    }
  }, [enabled]);

  useEffect(() => {
    const show = (event) => {
      selectionCallbackRef.current = event.detail?.onSelect || null;
      setError("");
      setRequested(true);
    };
    window.addEventListener(OPEN_DELIVERY_LOCATION_EVENT, show);
    return () => window.removeEventListener(OPEN_DELIVERY_LOCATION_EVENT, show);
  }, []);

  useEffect(() => {
    if (!open || guest) return undefined;
    let active = true;
    setAddressesLoading(true);
    setAddressesFailed(false);
    setAddresses([]);
    if (addressRequestRef.current?.session !== session) {
      addressRequestRef.current = { session, promise: dispatch(getUserAddress(new FormData())) };
    }
    const request = addressRequestRef.current.promise;
    request.then((response) => {
      if (!isSuccessResponse(response)) throw response;
      if (active) setAddresses(getAddresses(response).map(toAddressLocation).filter(hasSelectedDeliveryLocation));
    }).catch(() => {
      if (active) setAddressesFailed(true);
    }).finally(() => {
      if (active) setAddressesLoading(false);
      if (addressRequestRef.current?.promise === request) addressRequestRef.current = null;
    });
    return () => { active = false; };
  }, [dispatch, guest, open, session]);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        selectionCallbackRef.current = null;
        setRequested(false);
        setDismissedSession(session);
      }
      if (event.key === "Tab") {
        const controls = [...dialogRef.current.querySelectorAll("button:not(:disabled), input:not(:disabled), a[href]")];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!controls.includes(document.activeElement)) {
          event.preventDefault();
          first?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      operationRef.current += 1;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown, true);
      previousFocus?.focus();
    };
  }, [open, session]);

  useEffect(() => {
    if (!open) setBusy("");
  }, [open]);

  const close = () => {
    selectionCallbackRef.current = null;
    setRequested(false);
    setDismissedSession(session);
  };

  const select = (location) => {
    if (!hasSelectedDeliveryLocation(location)) return;
    const onSelect = selectionCallbackRef.current;
    dispatch(setLocation({ ...location, location_owner_id: session }));
    close();
    onSelect?.(location);
  };

  const detect = async () => {
    if (busy) return;
    const operation = ++operationRef.current;
    setBusy("detect");
    setError("");
    try {
      const location = await detectDeliveryLocation();
      if (operation === operationRef.current) select(location);
    } catch (failure) {
      if (operation === operationRef.current) setError(failure?.code === 1
        ? "Location access is blocked. Allow it in your browser settings, or enter your area below."
        : "We couldn't detect your location. Please enter your area below or try again.");
    } finally {
      if (operation === operationRef.current) setBusy("");
    }
  };

  const search = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (query.trim().length < 3) {
      setError("Please enter at least 3 characters to find your location.");
      return;
    }
    const operation = ++operationRef.current;
    setBusy("search");
    setError("");
    setResults([]);
    try {
      const locations = await searchDeliveryLocations(query);
      if (operation !== operationRef.current) return;
      setResults(locations);
      if (!locations.length) setError("No location found. Try a nearby area, city or pincode.");
    } catch (failure) {
      if (operation === operationRef.current) setError("We couldn't search for this location. Please try again.");
    } finally {
      if (operation === operationRef.current) setBusy("");
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="delivery-location-overlay">
      <div className="delivery-location-backdrop" onClick={close} aria-hidden="true" />
      <section ref={dialogRef} className="delivery-location-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="delivery-location-header">
          <span className="delivery-location-illustration" aria-hidden="true"><i className="ph ph-map-pin-line" /></span>
          <button type="button" className="delivery-location-close" onClick={close} aria-label="Close delivery location">
            <i className="ph ph-x" aria-hidden="true" />
          </button>
        </div>
        <h2 id={titleId}>Choose your location</h2>
        <p className="delivery-location-intro">Set your location to see nearby stores and delivery options.</p>
        <button type="button" className="delivery-location-detect" onClick={detect} disabled={Boolean(busy)}>
          <i className="ph ph-crosshair" aria-hidden="true" />
          {busy === "detect" ? "Detecting your location..." : "Use current location"}
          <i className="ph ph-caret-right" aria-hidden="true" />
        </button>
        <div className="delivery-location-divider"><span>or enter your location</span></div>
        <form className="delivery-location-search" onSubmit={search}>
          <label htmlFor={inputId}>Pincode, area or city</label>
          <div className="delivery-location-search-row">
            <input id={inputId} value={query} onChange={(event) => { setQuery(event.target.value); setResults([]); }} placeholder="Search pincode, area or city" autoComplete="off" disabled={Boolean(busy)} />
            <button type="submit" disabled={Boolean(busy)}>{busy === "search" ? "Searching..." : "Search"}</button>
          </div>
        </form>
        {error && <p className="delivery-location-error" role="alert">{error}</p>}
        {results.length > 0 && (
          <div className="delivery-location-list" aria-label="Matching locations">
            {results.map((item, index) => (
              <button type="button" key={item.place_id || index} onClick={() => select(item)} disabled={Boolean(busy)}>
                <i className="ph ph-map-pin" aria-hidden="true" /><span>{item.address}</span><i className="ph ph-caret-right" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
        {!guest && (
          <div className="delivery-location-saved">
            <h3>Saved addresses</h3>
            {addressesLoading ? <p role="status">Loading saved addresses...</p> : addresses.length > 0 ? (
              <div className="delivery-location-list">
                {addresses.map((item, index) => (
                  <button type="button" key={item.selected_address_id || index} onClick={() => select(item)} disabled={Boolean(busy)}>
                    <i className="ph ph-house" aria-hidden="true" />
                    <span><strong>{item.address_title || item.user_name || "Delivery address"}</strong>{item.address}</span>
                    <i className="ph ph-caret-right" aria-hidden="true" />
                  </button>
                ))}
              </div>
            ) : <p>{addressesFailed ? "Saved addresses couldn't be loaded. You can still choose your location above." : "No saved addresses yet. Choose your location above to get started."}</p>}
          </div>
        )}
        <p className="delivery-location-note"><i className="ph ph-shield-check" aria-hidden="true" /> Your location helps us show availability near you.</p>
      </section>
    </div>, document.body
  );
}
